import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import MainNavbar from "../../../components/MainNavbar/MainNavbar";
import HeaderPage from "../../../components/HeaderPage/HeaderPage";
import BreadcrumbPage from "../../../components/BreadcrumbPage/BreadcrumbPage";
import ArticleVote from "../../../components/Articles/ArticleVote/ArticleVote";
import ArticlesComments from "../../../components/Articles/ArticlesComments/ArticlesComments";
import { useArticles } from "../../../providers/Articles/ArticlesProvider";
import {
  ArticlesModel,
  ArticlesRelatedArticlesModel,
} from "../../../models/Articles/ArticleModel";
import { Card, Col, Container, Row } from "react-bootstrap";
import moment from "moment";
import "moment/locale/pt-br";
import ScrollTopButton from "../../../components/ScrollTopButton/ScrollTopButton";
import EditArticleButton from "../../../components/EditArticleButton/EditArticleButton";
import { BvIcon } from "bevi-icon";
import { ReactComponent as DocumentDeleteIcon } from "../../../assets/icons/document-delete.svg";
import "./ArticleView.scss";
import { createSlug } from "../../../@/components/tiptap-templates/simple/simple-editor";
import { useDocumentTitle } from "../../../hooks/useDocumentTitle";

function normalizeSpaces(s: string) {
  return s.replace(/\s+/g, " ").trim();
}

function stripAccents(s: string) {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function buildCollapsedTextAndMap(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const map: Array<{ node: Text; offset: number }> = [];
  let collapsed = "";
  let lastWasSpace = false;

  while (walker.nextNode()) {
    const t = walker.currentNode as Text;
    const val = t.nodeValue ?? "";
    for (let i = 0; i < val.length; i++) {
      const ch = val[i];
      const isSpace = /\s/.test(ch);
      if (isSpace) {
        if (!lastWasSpace) {
          collapsed += " ";
          map.push({ node: t, offset: i });
          lastWasSpace = true;
        }
      } else {
        collapsed += ch;
        map.push({ node: t, offset: i });
        lastWasSpace = false;
      }
    }
  }

  let startDrop = 0;
  let endDrop = 0;
  if (collapsed.startsWith(" ")) startDrop = 1;
  if (collapsed.endsWith(" ")) endDrop = 1;

  if (startDrop || endDrop) {
    collapsed = collapsed.slice(startDrop, collapsed.length - endDrop);
    map.splice(0, startDrop);
    if (endDrop) map.splice(map.length - endDrop, endDrop);
  }

  return { collapsed, map };
}

function wrapCollapsedRange(
  map: Array<{ node: Text; offset: number }>,
  start: number,
  end: number,
  index: number,
) {
  if (start < 0 || end <= start || end > map.length) return;

  const r = document.createRange();
  const startPos = map[start];
  const endPos = map[end - 1];

  r.setStart(startPos.node, startPos.offset);
  r.setEnd(endPos.node, endPos.offset + 1);

  const mark = document.createElement("mark");
  mark.setAttribute("data-highlight", "true");

  const frag = r.extractContents();
  mark.appendChild(frag);
  r.insertNode(mark);

  index === 0 && mark.scrollIntoView({ behavior: "smooth", block: "center" });
}

const ArticleView: FC = () => {
  const [article, setArticle] = useState<ArticlesModel | null>(null);
  const [status, setStatus] = useState<
    "loading" | "found" | "not-found" | "restricted"
  >("loading");
  const contentRef = useRef<HTMLDivElement | null>(null);

  const { id, chunk_index } = useParams();
  const { handleGet, handleGetVoteStatus, handleInsertView, getLastGetStatusCode } =
    useArticles();

  useDocumentTitle(article?.title);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setStatus("loading");

    handleGet(Number(id)).then((result) => {
      if (cancelled) return;
      setArticle(result);

      if (!result) {
        setStatus(getLastGetStatusCode() === 403 ? "restricted" : "not-found");
        return;
      }

      setStatus("found");
      handleGetVoteStatus(Number(id));

      // Artigos arquivados vistos por admin não devem disparar o tracking de
      // visualização: article_view() no backend segue rejeitando deleted_at
      // preenchido mesmo pra admin, o que voltaria a mostrar a tarja de erro.
      if (!result.deleted_at) {
        handleInsertView(Number(id));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  function applyHeadingIds(container: HTMLElement | null) {
    if (!container) return;
    const headings = container.querySelectorAll("h1, h2, h3, h4, h5, h6");
    headings.forEach((h) => {
      const text = h.textContent?.trim() ?? "";
      if (!text) return;
      const slug = createSlug(text);
      if (h.getAttribute("id") !== slug) {
        h.setAttribute("id", slug);
      }
    });
  }

  function decodeHtml(str: string) {
    const txt = document.createElement("textarea");
    txt.innerHTML = str;
    return txt.value;
  }

  function decodeHtmlFully(str: string, maxIterations = 5) {
    let current = str;
    for (let i = 0; i < maxIterations; i++) {
      const next = decodeHtml(current);
      if (next === current) break;
      current = next;
    }
    return current;
  }

  function splitContent(code: string) {
    const match = code.match(/\[\[SCRIPT\]\]([\s\S]*?)\[\[\/SCRIPT\]\]/);

    return {
      html: code.replace(/\[\[SCRIPT\]\][\s\S]*?\[\[\/SCRIPT\]\]/, ""),
      script: match?.[1] ?? "",
    };
  }

  function renderArticle(rawHtml: string, articleId?: string | number) {
    const container = document.createElement("div");
    container.innerHTML = rawHtml;

    container.querySelectorAll(".table-responsive").forEach((tableEl) => {
      if (tableEl.closest(".article-table-wrapper")) return;

      const wrapper = document.createElement("div");
      wrapper.className = "article-table-wrapper";
      wrapper.setAttribute("data-table-wrapper", "true");
      tableEl.replaceWith(wrapper);
      wrapper.appendChild(tableEl);
    });

    container.querySelectorAll("[data-html-preview]").forEach((el) => {
      const raw = el.getAttribute("data-html-preview");
      if (!raw) return;

      const decoded = decodeHtmlFully(raw);

      const { html, script } = splitContent(decoded);

      const iframeId = `iframe-${Math.random().toString(36).slice(2, 9)}`;
      const iframe = document.createElement("iframe");

      iframe.setAttribute(
        "sandbox",
        "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox",
      );
      iframe.setAttribute("data-iframe-id", iframeId);
      iframe.name = iframeId;

      iframe.style.width = "100%";
      iframe.style.border = "0";
      iframe.style.overflow = "hidden";
      iframe.style.height = "100px";

      const wrapper = document.createElement("div");
      wrapper.className = "article-iframe-wrapper";
      wrapper.appendChild(iframe);

      const iframeHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <link
            href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"
            rel="stylesheet"
          />
          <style>
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              overflow-x: auto;
              background-color: transparent !important;
              scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
            }

            ::-webkit-scrollbar {
              width: 8px;
              height: 8px;
            }

            ::-webkit-scrollbar-track {
              background: transparent;
            }

            ::-webkit-scrollbar-thumb {
              background: rgba(0, 0, 0, 0.15);
              border-radius: 4px;
            }

            ::-webkit-scrollbar-thumb:hover {
              background: rgba(0, 0, 0, 0.25);
            }

            #iframe-root {
              height: auto;
            }

            .highlighted-action-container,
            .dropdown-button,
            .dropdown-container,
            .styled-table {
              box-shadow: none !important;
            }

            table {
              width: max-content;
              min-width: 100%;
              border-collapse: collapse;
            }

            .table-responsive {
              width: 100%;
              padding-right: 0;
              overflow-x: auto;
              overflow-y: auto;
              -webkit-overflow-scrolling: touch;
              display: block;
              box-sizing: border-box;
              scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
            }

            .table-responsive::-webkit-scrollbar {
              width: 8px;
              height: 8px;
            }

            .table-responsive::-webkit-scrollbar-track {
              background: transparent;
            }

            .table-responsive::-webkit-scrollbar-thumb {
              background: rgba(0, 0, 0, 0.15);
              border-radius: 4px;
            }

            .table-responsive::-webkit-scrollbar-thumb:hover {
              background: rgba(0, 0, 0, 0.25);
            }

            th, td {
              border: 1px solid #dee2e6;
              padding: 0.75rem;
              white-space: nowrap;
            }

            #result-table-container {
              padding-left: 85px;
            }

            @media (max-width: 768px) {
              .table-responsive {
                padding-left: 16px !important;
              }

              #result-table-container {
                padding-left: 16px !important;
              }
            }
          </style>
        </head>
        <body>
          <div id="iframe-root" data-article-id="${articleId}">
            ${html}
          </div>

          <script>${script}</script>

          <script>
            let articleInsetLeft = 404;
            let resolvedMarginLeft = articleInsetLeft;
            let resolvedMarginRight = articleInsetLeft;
            let resolvedWidth = null;
            let lastResolvedInsetLeft = null;

            window.parent.postMessage(
              { type: "REQUEST_ARTICLE_INSET", iframeId: window.name },
              "*"
            );

            window.addEventListener("message", (event) => {
              if (
                event.data?.type === "ARTICLE_INSET" &&
                typeof event.data.insetLeft === "number" &&
                event.data.insetLeft !== lastResolvedInsetLeft
              ) {
                lastResolvedInsetLeft = event.data.insetLeft;
                articleInsetLeft = event.data.insetLeft;
                resolveHighlightedMargin();
                applyResponsiveIndent();
              }
            });

            function isMobileViewport() {
              return document.documentElement.clientWidth <= 767;
            }

            function getScrollLeft() {
              return document.documentElement.scrollLeft || document.body.scrollLeft || 0;
            }

            function resolveHighlightedMargin() {
              const highlightedContainer = document.querySelector(".highlighted-action-container");
              if (!highlightedContainer) return;

              if (isMobileViewport()) {
                highlightedContainer.style.removeProperty("width");
                highlightedContainer.style.removeProperty("margin-left");
                highlightedContainer.style.removeProperty("margin-right");
                resolvedWidth = null;
                return;
              }

              highlightedContainer.style.removeProperty("width");
              highlightedContainer.style.setProperty("margin-left", articleInsetLeft + "px", "important");

              const actualLeft = highlightedContainer.getBoundingClientRect().left + getScrollLeft();
              const drift = actualLeft - articleInsetLeft;

              resolvedMarginLeft = articleInsetLeft - drift;
              resolvedMarginRight = articleInsetLeft;
              resolvedWidth = document.documentElement.clientWidth - resolvedMarginLeft - resolvedMarginRight;
            }

            function resolveIndentPadding(el) {
              const containerLeft = el.getBoundingClientRect().left + getScrollLeft();
              return Math.max(0, articleInsetLeft - containerLeft);
            }

            function applyResponsiveIndent() {
              const indentTargets = [
                document.getElementById("result-table-container"),
                ...document.querySelectorAll(".table-responsive"),
              ].filter(Boolean);

              indentTargets.forEach((el) => {
                if (isMobileViewport()) {
                  el.style.removeProperty("padding-left");
                } else {
                  el.style.setProperty("padding-left", resolveIndentPadding(el) + "px", "important");
                }
              });

              const searchInput = document.getElementById("user-search-input");
              const resultContainer = document.getElementById("result-table-container");
              if (searchInput && resultContainer) {
                if (isMobileViewport()) {
                  searchInput.style.removeProperty("margin-left");
                  searchInput.style.width = "100%";
                } else {
                  const indent = resolveIndentPadding(resultContainer);
                  searchInput.style.setProperty("margin-left", indent + "px", "important");
                  searchInput.style.setProperty("width", "calc(100% - " + indent + "px)", "important");
                }
              }

              const highlightedContainer = document.querySelector(".highlighted-action-container");
              if (highlightedContainer) {
                highlightedContainer.style.setProperty("margin-top", "20px", "important");
                highlightedContainer.style.setProperty("margin-bottom", "20px", "important");

                if (isMobileViewport()) {
                  highlightedContainer.style.removeProperty("margin-left");
                  highlightedContainer.style.removeProperty("margin-right");
                  highlightedContainer.style.removeProperty("width");
                  highlightedContainer.style.removeProperty("box-sizing");
                } else {
                  highlightedContainer.style.setProperty("margin-left", resolvedMarginLeft + "px", "important");
                  highlightedContainer.style.setProperty("margin-right", resolvedMarginRight + "px", "important");
                  if (resolvedWidth !== null) {
                    highlightedContainer.style.setProperty("width", resolvedWidth + "px", "important");
                    highlightedContainer.style.setProperty("box-sizing", "border-box", "important");
                  }
                }
              }
            }

            let lastHeight = -1;
            const root = document.getElementById("iframe-root");

            function getHeight() {
              return root ? root.scrollHeight : 0;
            }

            function sendHeight(force = false) {
              requestAnimationFrame(() => {
                const height = getHeight();
                if (force || height !== lastHeight) {
                  lastHeight = height;
                  window.parent.postMessage(
                    { type: "IFRAME_HEIGHT", height, iframeId: window.name },
                    "*"
                  );
                }
              });
            }

            const observer = new MutationObserver(() => {
              applyResponsiveIndent();
              sendHeight(true);
            });
            observer.observe(root, {
              childList: true,
              subtree: true,
              attributes: true,
              characterData: true
            });

            window.addEventListener("load", () => {
              setTimeout(() => {
                applyResponsiveIndent();
                sendHeight(true);
              }, 100);
            });
            window.addEventListener("resize", () => sendHeight(true));

            setTimeout(() => {
              applyResponsiveIndent();
              sendHeight(true);
            }, 100);
          </script>
        </body>
        </html>
        `;

      iframe.src = URL.createObjectURL(
        new Blob([iframeHtml], { type: "text/html" })
      );

      el.replaceWith(wrapper);
    });

    return container.innerHTML;
  }

  const lastSearchMatch = useMemo(() => {
    try {
      if (chunk_index == undefined) return null;

      const raw = localStorage.getItem("articles_search:last");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const aid = Number(id);
      const cidx = Number(chunk_index);
      return (
        parsed.articles?.find(
          (a: any) =>
            a.article_id === aid &&
            (Number.isFinite(cidx) ? (a.chunk_index ?? 0) === cidx : true),
        ) ?? null
      );
    } catch {
      return null;
    }
  }, [id, chunk_index]);

  useEffect(() => {
    const chunk = lastSearchMatch?.chunk?.trim();
    const root = contentRef.current;
    if (!chunk || !root || !article?.content) return;

    if (root.querySelector('mark[data-highlight="true"]')) return;

    const t = setTimeout(() => {
      const { collapsed, map } = buildCollapsedTextAndMap(root);

      const norm = (s: string) =>
        stripAccents(normalizeSpaces(s)).toLowerCase();
      const hay = norm(collapsed);

      const blocks = chunk
        .split(/\r?\n+/g)
        .map((s: string) => s.replace(/^\s*[-•]\s*/, "").trim())
        .filter(Boolean);

      if (blocks.length === 0) return;

      const needles = blocks.map(norm);
      const MAX_GAP = 100;

      // 2) encontra melhores pontos de início (todas as ocorrências do 1º bloco)
      const starts: number[] = [];
      {
        let idx = hay.indexOf(needles[0], 0);
        let guard = 0;
        while (idx !== -1 && guard < 200) {
          starts.push(idx);
          const nextFrom = idx + Math.max(1, needles[0].length);
          idx = hay.indexOf(needles[0], nextFrom);
          guard++;
        }
      }

      // 3) pontua cada candidata pelo nº de blocos em sequência que casam
      type Cand = { start: number; matched: number; spanEnd: number };
      const cands: Cand[] = starts.map((start) => {
        let from = start + needles[0].length;
        let matched = 1;
        let spanEnd = from;
        for (let i = 1; i < needles.length; i++) {
          const pos = hay.indexOf(needles[i], from);
          if (pos === -1 || pos - from > MAX_GAP) break;
          matched++;
          from = pos + needles[i].length;
          spanEnd = from;
        }
        return { start, matched, spanEnd };
      });

      // 4) escolhe a melhor: mais blocos casados; empate => menor start
      cands.sort((a, b) => {
        if (b.matched !== a.matched) return b.matched - a.matched;
        return a.start - b.start;
      });

      // se nenhuma candidata, tenta chunk inteiro como fallback
      if (cands.length === 0) {
        const whole = norm(chunk);
        let pos = hay.indexOf(whole);
        if (pos === -1 && whole.length > 2000) {
          const head = whole.slice(0, 2000);
          pos = hay.indexOf(head);
          if (pos !== -1)
            wrapCollapsedRange(map, pos, pos + head.length, 0);
        } else if (pos !== -1) {
          wrapCollapsedRange(map, pos, pos + whole.length, 0);
        }
        return;
      }

      const best = cands[0];

      // 5) com o melhor início, grifa os blocos em sequência
      let from = best.start;
      let hits = 0;
      for (let i = 0; i < needles.length; i++) {
        const pos = hay.indexOf(needles[i], from);
        if (pos === -1 || (i > 0 && pos - from > MAX_GAP)) break;
        wrapCollapsedRange(map, pos, pos + needles[i].length, i);
        from = pos + needles[i].length;
        hits++;
      }

      // 6) fallback leve: se só o 1º bloco casou, tenta estender com mais texto
      if (hits === 1 && needles[0].length < 2000) {
        const headExtra = Math.min(2000, needles[0].length + 400);
        const pos = best.start;
        wrapCollapsedRange(
          map,
          pos,
          Math.min(pos + headExtra, hay.length),
          0,
        );
      }
    }, 0);

    return () => clearTimeout(t);
  }, [article?.content, lastSearchMatch]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    applyHeadingIds(root);

    let timer: number | null = null;
    let tableTimer: number | null = null;
    let isMounted = true;

    const debouncedApply = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (isMounted) {
          applyHeadingIds(root);
        }
        timer = null;
      }, 80);
    };

    const observer = new MutationObserver(() => {
      debouncedApply();
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: false,
    });

    tableTimer = window.setTimeout(() => {
      if (!isMounted || !root) return;

      const tableWrappers = root.querySelectorAll("[data-table-wrapper]");
      if (!tableWrappers.length) return;

      const isMobile = window.innerWidth <= 768;

      tableWrappers.forEach((wrapper) => {
        const tableResponsive = wrapper.querySelector(".table-responsive") as HTMLElement | null;
        if (!tableResponsive) return;

        if (isMobile) {
          tableResponsive.style.removeProperty("padding-left");
        } else {
          const inset = Math.max(
            0,
            root.getBoundingClientRect().left - (wrapper as HTMLElement).getBoundingClientRect().left,
          );
          if (inset > 0) {
            tableResponsive.style.setProperty("padding-left", `${inset}px`, "important");
          }
        }
      });
    }, 300);

    return () => {
      isMounted = false;
      observer.disconnect();
      if (timer) window.clearTimeout(timer);
      if (tableTimer) window.clearTimeout(tableTimer);
    };
  }, [article?.content]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    function handleAnchorClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target) return;

      const link = target.closest("a[href^='#']") as HTMLAnchorElement | null;
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href || href === "#") return;

      e.preventDefault();

      const targetId = href.substring(1);
      const element = document.getElementById(targetId);
      if (!element) return;

      const navbar = document.getElementById("navbarBeviLearn");
      const navbarHeight = navbar?.offsetHeight ?? 0;

      const elementTop =
        element.getBoundingClientRect().top +
        window.scrollY -
        navbarHeight -
        50;

      window.scrollTo({
        top: elementTop,
        behavior: "smooth",
      });
    }

    root.addEventListener("click", handleAnchorClick);

    return () => {
      root.removeEventListener("click", handleAnchorClick);
    };
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const excelTables = contentRef.current.querySelectorAll("excel-table");

    excelTables.forEach((el) => {
      const raw = el.getAttribute("data");
      if (!raw) return;

      const { columns, rows } = JSON.parse(raw);

      const pageSize = 5;
      let currentPage = 0;
      const totalPages = Math.ceil(rows.length / pageSize);

      const wrapper = document.createElement("div");
      wrapper.className = "excel-wrapper";

      const table = document.createElement("table");
      table.className = "excel-read-table";

      const thead = document.createElement("thead");
      const headRow = document.createElement("tr");

      columns.forEach((col: string) => {
        const th = document.createElement("th");
        th.textContent = col;
        headRow.appendChild(th);
      });

      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = document.createElement("tbody");
      table.appendChild(tbody);

      const controls = document.createElement("div");
      controls.className = "excel-pagination-controls";

      const pagination = document.createElement("div");
      pagination.className = "excel-pagination";

      const resultInfo = document.createElement("div");
      resultInfo.className = "excel-result-info";

      const prev = document.createElement("button");
      prev.textContent = "<";
      prev.className = "button-pages";

      const pageInfo = document.createElement("span");
      pageInfo.className = "excel-page-info";

      const next = document.createElement("button");
      next.textContent = ">";
      next.className = "button-pages";

      controls.append(prev, pageInfo, next);
      pagination.append(resultInfo, controls);
      wrapper.append(table, pagination);
      el.replaceWith(wrapper);

      const renderPage = () => {
        tbody.innerHTML = "";

        const start = currentPage * pageSize;
        const pageRows = rows.slice(start, start + pageSize);

        pageRows.forEach((row: any[]) => {
          const tr = document.createElement("tr");

          row.forEach((cell) => {
            const td = document.createElement("td");
            td.textContent = String(cell);
            tr.appendChild(td);
          });

          tbody.appendChild(tr);
        });

        pageInfo.textContent = `${currentPage + 1} / ${totalPages}`;
        resultInfo.textContent = `Resultados por página: ${pageRows.length}`;

        prev.disabled = currentPage === 0;
        next.disabled = currentPage + 1 === totalPages;
      };

      prev.onclick = () => {
        if (currentPage > 0) {
          currentPage--;
          renderPage();
        }
      };

      next.onclick = () => {
        if (currentPage + 1 < totalPages) {
          currentPage++;
          renderPage();
        }
      };

      renderPage();
    });
  }, [article?.content]);

  useEffect(() => {
    const trackedIframeIds = new Set<string>();
    let debounceTimer: number | null = null;
    let resizeRaf = 0;
    const timers: number[] = [];
    let isMounted = true;

    function computeInsetLeft(reference: HTMLElement) {
      const root = contentRef.current;
      if (!root) return 0;
      return Math.max(
        0,
        root.getBoundingClientRect().left - reference.getBoundingClientRect().left,
      );
    }

    function applyTableIndent() {
      if (!isMounted || !contentRef.current) return;

      const tableWrappers = contentRef.current.querySelectorAll("[data-table-wrapper]");
      if (!tableWrappers.length) return;

      const isMobile = window.innerWidth <= 768;

      tableWrappers.forEach((wrapper) => {
        const tableResponsive = wrapper.querySelector(".table-responsive") as HTMLElement | null;
        if (!tableResponsive) return;

        const hasScroll = tableResponsive.scrollWidth > tableResponsive.clientWidth;
        wrapper.classList.toggle("has-scroll", hasScroll);

        if (isMobile) {
          tableResponsive.style.removeProperty("padding-left");
        } else {
          const inset = computeInsetLeft(wrapper as HTMLElement);
          if (inset > 0) {
            tableResponsive.style.setProperty("padding-left", `${inset}px`, "important");
          }
        }
      });
    }

    function sendInset(iframeId: string) {
      if (!isMounted) return;

      const iframe = document.querySelector(`[data-iframe-id="${iframeId}"]`) as HTMLIFrameElement | null;
      if (!iframe?.contentWindow) return;

      iframe.contentWindow.postMessage(
        { type: "ARTICLE_INSET", insetLeft: computeInsetLeft(iframe.closest(".article-iframe-wrapper") ?? iframe) },
        "*",
      );
    }

    function handleMessage(event: MessageEvent) {
      if (!isMounted) return;

      if (
        event.data?.type === "IFRAME_HEIGHT" &&
        typeof event.data.height === "number" &&
        event.data.iframeId
      ) {
        const newHeight = Math.max(100, event.data.height);

        if (debounceTimer) window.clearTimeout(debounceTimer);

        debounceTimer = window.setTimeout(() => {
          if (!isMounted) return;

          const iframe = document.querySelector(`[data-iframe-id="${event.data.iframeId}"]`) as HTMLIFrameElement;
          if (iframe) {
            iframe.style.height = `${newHeight}px`;
          }
          debounceTimer = null;

          requestAnimationFrame(() => {
            if (!isMounted) return;
            trackedIframeIds.forEach(sendInset);
            applyTableIndent();
          });
        }, 100);
        return;
      }

      if (event.data?.type === "REQUEST_ARTICLE_INSET" && event.data.iframeId) {
        trackedIframeIds.add(event.data.iframeId);
        sendInset(event.data.iframeId);
      }
    }

    function handleResize() {
      if (!isMounted) return;

      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        if (!isMounted) return;
        trackedIframeIds.forEach(sendInset);
        applyTableIndent();
      });
    }

    const timer1 = window.setTimeout(() => {
      if (isMounted) applyTableIndent();
    }, 200);
    timers.push(timer1);

    const timer2 = window.setTimeout(() => {
      if (isMounted) applyTableIndent();
    }, 500);
    timers.push(timer2);

    window.addEventListener("message", handleMessage);
    window.addEventListener("resize", handleResize);

    return () => {
      isMounted = false;
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("resize", handleResize);
      if (debounceTimer) window.clearTimeout(debounceTimer);
      cancelAnimationFrame(resizeRaf);
      timers.forEach(timer => window.clearTimeout(timer));
    };
  }, []);

  return (
    <>
      <MainNavbar />
      <div
        className={`ArticleView${status === "not-found" || status === "restricted" ? " ArticleView--not-found" : ""}`}
        data-testid="ArticleView"
      >
        {status === "not-found" || status === "restricted" ? (
          <>
            <HeaderPage title="Página não encontrada" subtitle="" />
            <div className="content-wrapper content-wrapper--not-found">
              <Container>
                <Row className="justify-content-center align-items-center py-sm-5 py-3">
                  <Col lg={8} className="text-center article-not-found">
                    <DocumentDeleteIcon width={89} height={106} />
                    {status === "restricted" ? (
                      <>
                        <h2>Conteúdo restrito</h2>
                        <p>
                          Este artigo está disponível somente para usuários internos.
                        </p>
                      </>
                    ) : (
                      <>
                        <h2>Artigo excluído ou arquivado</h2>
                        <p>
                          Este artigo não está mais disponível. Ele pode ter sido removido
                          <br />
                          ou arquivado pela equipe responsável.
                        </p>
                      </>
                    )}
                  </Col>
                </Row>
              </Container>
            </div>
          </>
        ) : (
          <>
            <HeaderPage
              title={article?.category?.name ?? ""}
              subtitle={article?.category?.description ?? ""}
            />

            <div className="breadcrumb-wrapper">
              <Container>
                <Row className="justify-content-center">
                  <Col md={10}>
                    <BreadcrumbPage
                      category={article?.category}
                      section={article?.section}
                      subsection={article?.subsection}
                    />
                  </Col>
                </Row>
              </Container>
            </div>

            <div className="content-wrapper">
              <Container>
                <Row className="justify-content-center py-sm-5 py-3">
                  <Col lg={10}>
                    <Row>
                      <Col md={12}>
                        <h2>{article?.title ?? ""}</h2>
                        <p className="text-sm">
                          Atualizado{" "}
                          {moment(article?.updated_at).locale("pt-br").fromNow()}.
                        </p>

                        <div
                          className="content"
                          ref={contentRef}
                          dangerouslySetInnerHTML={{
                            __html: renderArticle(article?.content ?? "", id),
                          }}
                        />

                        <hr className="separator-actions" />
                        <ArticleVote articleId={article?.id || 0} />

                        {article?.related_articles && article.related_articles.length > 0 && (
                          <Card className="articles-related-card">
                            <Card.Body>
                              <Card.Title className="mb-4">
                                Artigos relacionados
                              </Card.Title>

                              <div className="articles-related-grid">
                                {article?.related_articles?.map(
                                  (
                                    item: ArticlesRelatedArticlesModel,
                                    index: number,
                                  ) => (
                                    <div key={item?.related_article?.id || index} className="related-article-item">
                                      <a
                                        href={`/articles/view/${item?.related_article?.id}`}
                                      >
                                        <BvIcon name="chevronForward" />
                                        <Card.Text>
                                          {item?.related_article?.title ?? ""}
                                        </Card.Text>
                                      </a>
                                    </div>
                                  ),
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        )}
                        <ArticlesComments articleId={article?.id || 0} />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Container>
            </div>
          </>
        )}

        <ScrollTopButton />
        {status !== "not-found" && status !== "restricted" && <EditArticleButton />}
      </div>
    </>
  );
};

export default ArticleView;
